import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  useColorModeValue,
  Icon,
  InputGroup,
  InputRightElement,
  Alert,
  AlertIcon,
  IconButton,
  Textarea,
} from "@chakra-ui/react";
import { useButtonProps } from "hooks";
import React from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { BiTrash } from "react-icons/bi";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { MdDragIndicator } from "react-icons/md";
import type { UpsertCopyForm } from "./UpsertCopy";
import { JSONEditor } from "screens/common/components";

export const CopiesFieldArray = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<UpsertCopyForm>();

  const commonButtonProps = useButtonProps("sm", "secondary");
  const buttonColor = useColorModeValue("gray.500", "gray.600");
  const buttonHoverColor = useColorModeValue("gray.600", "gray.400");
  const deleteButtonProps = useButtonProps("sm", "secondary");
  const dragHandleProps = useButtonProps("sm", "secondary");
  const bgColor = useColorModeValue("#fbfbfb", "gray.800");

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "listValue",
  });

  const isAnyValueJson = fields.some((field) => {
    const currentValue = field.value;
    let isJsonValue = false;
    let parsedJson: any = null;
    // Try to parse the value as JSON
    if (typeof currentValue === "string") {
      try {
        parsedJson = JSON.parse(currentValue);
        isJsonValue = typeof parsedJson === "object" && parsedJson !== null;
      } catch (e) {
        // Not valid JSON, will use regular input
      }
    }
    return isJsonValue;
  });

  return (
    <>
      <Button {...commonButtonProps} mb={"1rem"} onClick={() => append({ value: "" })}>
        Add Value
      </Button>

      <Box>
        {errors.listValue?.message && (
          <Alert status="error">
            <AlertIcon />
            {errors.listValue?.message}
          </Alert>
        )}
        <DragDropContext
          onDragEnd={(result) => {
            if (!result.destination) return;
            move(result.source.index, result.destination.index);
          }}>
          <Droppable droppableId="copies">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {fields.map((field, index) => (
                  <Draggable key={field.id} draggableId={field.id} index={index}>
                    {(provided) => (
                      <Flex
                        {...provided.draggableProps}
                        style={{ ...provided.draggableProps.style, left: "auto !important", top: "auto !important" }}
                        ref={provided.innerRef}
                        alignItems="center"
                        py="1"
                        borderRadius="md">
                        <IconButton
                          {...dragHandleProps}
                          mt="-1rem"
                          _hover={{}}
                          border={"none"}
                          aria-label="Drag handle"
                          icon={<Icon as={MdDragIndicator} boxSize="1.3rem" _hover={{ color: "primary.default" }} />}
                          {...provided.dragHandleProps}
                          cursor="grab"
                        />
                        <Controller
                          name={`listValue.${index}.value`}
                          control={control}
                          render={({ field }) => {
                            const currentValue = field.value;

                            // Format JSON for better readability
                            const getFormattedJsonValue = (value: string) => {
                              if (typeof value === "string") {
                                try {
                                  const parsed = JSON.parse(value);
                                  return JSON.stringify(parsed, null, 2);
                                } catch (e) {
                                  return value;
                                }
                              }
                              return value;
                            };

                            // Handle JSON editor onChange to maintain formatting
                            const handleJsonChange = (newValue: string) => {
                              try {
                                // Try to parse and minify the JSON for storage
                                const parsed = JSON.parse(newValue);
                                const minified = JSON.stringify(parsed);
                                field.onChange(minified);
                              } catch (e) {
                                // If invalid JSON, store as-is
                                field.onChange(newValue);
                              }
                            };

                            return (
                              <FormControl isInvalid={!!(errors.listValue && errors.listValue[index])} pb="1rem">
                                {isAnyValueJson ? (
                                  <Box position="relative" mb={2}>
                                    <JSONEditor
                                      onChange={handleJsonChange}
                                      value={getFormattedJsonValue(currentValue)}
                                      height="auto"
                                      backgroundColor={bgColor}
                                      showSchemaButton={false}
                                    />
                                    <Box position="absolute" top={1} right={1}>
                                      <Icon
                                        cursor="pointer"
                                        as={BiTrash}
                                        color={buttonColor}
                                        boxSize="1rem"
                                        _hover={{ color: buttonHoverColor }}
                                        onClick={() => remove(index)}
                                      />
                                    </Box>
                                  </Box>
                                ) : (
                                  <FormControl flex={1}>
                                    {/* Check if content might be markdown (contains markdown syntax) */}
                                    {currentValue &&
                                    (currentValue.includes("**") ||
                                      currentValue.includes("*") ||
                                      currentValue.includes("#") ||
                                      currentValue.includes("[") ||
                                      currentValue.includes("`")) ? (
                                      <Box position="relative">
                                        <Textarea
                                          {...field}
                                          placeholder="Write copy here... (Supports Markdown)"
                                          size="sm"
                                          rows={3}
                                          resize="vertical"
                                          pr="2rem"
                                        />
                                        <Box position="absolute" top={1} right={1}>
                                          <IconButton
                                            {...deleteButtonProps}
                                            aria-label="Delete copy"
                                            icon={<Icon as={BiTrash} boxSize="1rem" />}
                                            onClick={() => remove(index)}
                                            size="xs"
                                            variant="ghost"
                                          />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <InputGroup size="sm">
                                        <Input {...field} placeholder="Write copy here..." size="sm" pr="2rem" type="text" />
                                        <InputRightElement width="2rem">
                                          <IconButton
                                            {...deleteButtonProps}
                                            aria-label="Delete copy"
                                            icon={<Icon as={BiTrash} boxSize="1rem" />}
                                            onClick={() => remove(index)}
                                            size="xs"
                                            variant="ghost"
                                          />
                                        </InputRightElement>
                                      </InputGroup>
                                    )}
                                  </FormControl>
                                )}
                                {errors.listValue && errors.listValue[index]?.value?.message && (
                                  <FormErrorMessage>{errors.listValue[index]?.value?.message}</FormErrorMessage>
                                )}
                              </FormControl>
                            );
                          }}
                        />
                      </Flex>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </>
  );
};
